// Competitions build — shared UI tokens + pure helpers for the embeddable
// Schedule / Stats components (UI-SPEC.md §1, §8, §9, §22).
//
// Everything here is deterministic and prop-driven: no API calls, no page
// context. Colors are the handoff's verbatim hex values (UI-SPEC §1.2).

import { formatRating10 } from "../../totw/totwUtils";

export const C = {
  accent: "#2183FE",
  accentHover: "#0B5FCB",
  ink900: "#0F172A",
  ink700: "#334155",
  ink600: "#475569",
  ink500: "#64748B",
  ink400: "#94A3B8",
  grid: "#EDF1F6",
  border: "#E2E8F0",
  rowDivider: "#F4F7FA",
  tableHeadBg: "#F8FAFC",
  rowHoverBg: "#F8FBFF",
  placeholderBg: "#F1F5FA",
};

export const FONT_FAMILY =
  "'Helvetica Neue',Helvetica,Arial,sans-serif";

// Score separator per UI-SPEC §22: `"3 : 0"` — a space around the colon.
export const scoreText = (home, away) =>
  home == null || away == null ? null : `${home} : ${away}`;

// Minutes per UI-SPEC §22: "1,980" (en-US grouping on every board).
export const formatMinutes = (minutes) => {
  if (minutes == null || minutes === "") return null;
  const n = Number(minutes);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString("en-US");
};

// Rating display on the competitions boards: TWO decimals ("8.34",
// UI-SPEC §22 — binding sample formats), while the chip COLOR comes from the
// platform's single tier source ratingTier10() (totwUtils, reused, never
// re-implemented). formatRating10's null/zero gating is reused so a
// provider "0.0" artefact renders NO chip, exactly like on the match rail.
export const formatRating2 = (v) => {
  const gated = formatRating10(v);
  if (gated == null) return null;
  return Number(v).toFixed(2);
};

// Date/time per UI-SPEC §22: "Sat 21 Feb · 15:30"; time cell shows "TBD"
// when the kickoff time is undetermined. `date` accepts a Date, an ISO
// string or a unix-seconds number (TheSports match_time).
export const toDate = (date) => {
  if (date == null || date === "") return null;
  if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;
  if (typeof date === "number") {
    // Heuristic: unix SECONDS (TheSports) vs milliseconds.
    return new Date(date < 1e12 ? date * 1000 : date);
  }
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatMatchDate = (date, locale = "en") => {
  const d = toDate(date);
  if (!d) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(d);
  } catch (_) {
    return d.toDateString();
  }
};

export const formatKickoffTime = (date) => {
  const d = toDate(date);
  if (!d) return null;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

// Initials for crest/photo fallbacks ("no n/a anywhere").
export const initialsOf = (name, short) => {
  const src = (short || name || "").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};
